'use client';

import type { StrapiNewsItem } from '@outpoll/strapi-client';
import { useFormatter, useNow } from 'next-intl';
import { useSearch } from '../core/SearchContext';
import { Highlight } from '../shared/Highlight';
import { ResultThumb } from '../shared/ResultThumb';
import { RowText, SearchRow } from '../shared/SearchRow';

const NEWS_FALLBACK_IMAGE = '/news-fallback.webp';
const THUMB_WIDTH = 40;
const THUMB_HEIGHT = 28;
const MINUTE_MS = 60_000;

/** The article's page in the news zone; the feed without a category to file it under. */
function newsHref(item: StrapiNewsItem): string {
  const category = item.category?.slug;
  return category ? `/news/${category.toLowerCase()}/${item.slug}` : '/news';
}

interface NewsRowProps {
  item: StrapiNewsItem;
}

/** An article: its picture, title and how long ago it went out. */
export function NewsRow({ item }: NewsRowProps) {
  const format = useFormatter();
  const now = useNow({ updateInterval: MINUTE_MS });
  const { select } = useSearch();
  const href = newsHref(item);

  const handleSelect = () =>
    select({ id: `news:${item.id}`, kind: 'news', label: item.title, href });

  return (
    <SearchRow href={href} onSelect={handleSelect}>
      <ResultThumb
        src={item.image || null}
        fallback={NEWS_FALLBACK_IMAGE}
        width={THUMB_WIDTH}
        height={THUMB_HEIGHT}
        className="h-7 w-10 rounded-[5px]"
      />
      <RowText
        title={<Highlight text={item.title} />}
        meta={format.relativeTime(new Date(item.publishedAt), now)}
      />
    </SearchRow>
  );
}
